import React, { useState, useEffect, useMemo, useCallback } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { Header, formatScheduleTime } from "../../components/modules";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { Switch } from "../../components/ui";
import { Spinner } from "reactstrap";
import { resizeImage } from "../../util";
import dayjs from "dayjs";
import {
  FiSettings,
  FiPlus,
  FiTrash2,
  FiSearch,
  FiCheck,
  FiX,
  FiEdit2,
  FiSave,
  FiRotateCcw,
  FiTrendingUp,
  FiTrendingDown,
  FiHelpCircle,
  FiDollarSign,
  FiTag,
  FiCreditCard,
  FiGlobe,
  FiClock,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiZap,
} from "react-icons/fi";
import { FaPlus } from "react-icons/fa";

// ฟังก์ชันวิเคราะห์ประเภทของ Pattern เลข
const getPatternInfo = (pattern = "") => {
  const p = pattern.trim().toLowerCase();
  if (/^x{4}\d{2}$/.test(p)) {
    return {
      type: "end2",
      label: "ท้าย 2 ตัว",
      badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
      dotClass: "bg-blue-500",
    };
  }
  if (/^x{3}\d{3}$/.test(p)) {
    return {
      type: "end3",
      label: "ท้าย 3 ตัว",
      badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
      dotClass: "bg-purple-500",
    };
  }
  if (/^\d{3}x{3}$/.test(p)) {
    return {
      type: "front3",
      label: "หน้า 3 ตัว",
      badgeClass: "bg-cyan-50 text-cyan-700 border-cyan-200",
      dotClass: "bg-cyan-500",
    };
  }
  if (/^\d{6}$/.test(p)) {
    return {
      type: "exact6",
      label: "เลข 6 หลักตรง",
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dotClass: "bg-emerald-500",
    };
  }
  return {
    type: "custom",
    label: "กำหนดเอง",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
  };
};

// ฟังก์ชัน Smart Parser: แปลงข้อความที่พิมพ์เป็น Pattern ให้อัตโนมัติ
const parseInputToPatterns = (inputStr) => {
  if (!inputStr || !inputStr.trim()) return [];
  const tokens = inputStr
    .split(/[\s,\n]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  const results = [];
  for (const token of tokens) {
    if (token.includes("x")) {
      if (token.length === 6 && !results.includes(token)) {
        results.push(token);
      }
      continue;
    }

    const digitsOnly = token.replace(/\D/g, "");
    if (!digitsOnly) continue;

    let p = "";
    if (digitsOnly.length === 2) {
      p = `xxxx${digitsOnly}`;
    } else if (digitsOnly.length === 3) {
      p = `xxx${digitsOnly}`;
    } else if (digitsOnly.length === 6) {
      p = digitsOnly;
    } else if (digitsOnly.length === 1) {
      p = `xxxx0${digitsOnly}`;
    }

    if (p && !results.includes(p)) {
      results.push(p);
    }
  }
  return results;
};

export const Prices = () => {
  const { callApi, updateConfig, configs, db } = Context();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  // ── Panel 1: Base Price & Fee States ──
  const [basePrice, setBasePrice] = useState(configs?.price ?? 80);
  const [serviceFee, setServiceFee] = useState(configs?.fee ?? 20);

  // ── Panel 2: Special Prices States ──
  const [specialPricesMap, setSpecialPricesMap] = useState({});
  const [originalSpecialPrices, setOriginalSpecialPrices] = useState({});
  const [inputNumber, setInputNumber] = useState("");
  const [inputAdj, setInputAdj] = useState("20");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  // ── Universal Queue (Schedule) States ──
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [pendingSchedules, setPendingSchedules] = useState([]);
  const [showQueueBanner, setShowQueueBanner] = useState(true);

  // ── Fee Schedule States ──
  const [isFeeScheduleModalOpen, setIsFeeScheduleModalOpen] = useState(false);
  const [feeScheduleTarget, setFeeScheduleTarget] = useState("");
  const [feeScheduleStart, setFeeScheduleStart] = useState("");

  // ── Panel 4: Receiving Bank Form States ──
  const [thaiBankName, setThaiBankName] = useState("");
  const [thaiAccName, setThaiAccName] = useState("");
  const [thaiAccNo, setThaiAccNo] = useState("");

  const [laoLakBankName, setLaoLakBankName] = useState("");
  const [laoLakAccName, setLaoLakAccName] = useState("");
  const [laoLakAccNo, setLaoLakAccNo] = useState("");

  const [laoThbBankName, setLaoThbBankName] = useState("");
  const [laoThbAccName, setLaoThbAccName] = useState("");
  const [laoThbAccNo, setLaoThbAccNo] = useState("");

  // Synchronize state with configs
  useEffect(() => {
    if (configs) {
      if (configs.price !== undefined) setBasePrice(Number(configs.price));
      if (configs.fee !== undefined) setServiceFee(Number(configs.fee));

      if (configs.specialPrices && typeof configs.specialPrices === "object") {
        const data = { ...configs.specialPrices };
        setSpecialPricesMap(data);
        setOriginalSpecialPrices(data);
      } else {
        setSpecialPricesMap({});
        setOriginalSpecialPrices({});
      }

      if (configs.payment) {
        setThaiBankName(configs.payment.thaiBank?.bankName || "ธนาคารกสิกรไทย");
        setThaiAccName(configs.payment.thaiBank?.accName || "บจก. วันดี ออนไลน์");
        setThaiAccNo(configs.payment.thaiBank?.accNo || "210-1-08629-4");

        setLaoLakBankName(
          configs.payment.laoBankLak?.bankName ||
            configs.payment.laoBank?.name ||
            "Joint Development Bank (JDB)"
        );
        setLaoLakAccName(
          configs.payment.laoBankLak?.accName ||
            configs.payment.laoBank?.accName ||
            "MISS THITAPA THANASAPPREECHA"
        );
        setLaoLakAccNo(
          configs.payment.laoBankLak?.accNo ||
            configs.payment.laoBank?.accNo ||
            "00120010010080447"
        );

        setLaoThbBankName(
          configs.payment.laoBankThb?.bankName || "Joint Development Bank (JDB)"
        );
        setLaoThbAccName(
          configs.payment.laoBankThb?.accName || "MISS THITAPA THANASAPPREECHA"
        );
        setLaoThbAccNo(
          configs.payment.laoBankThb?.accNo || "00120010210030720"
        );
      }
    }
  }, [configs]);

  // Fetch Pending Schedules from Universal Queue
  const fetchPendingSchedules = useCallback(async () => {
    try {
      if (!callApi) return;
      const res = await callApi("schedules-list", {
        status: "pending",
      });
      if (res && res.status && Array.isArray(res.data)) {
        setPendingSchedules(res.data);
      }
    } catch (err) {
      console.error("[Prices] Error fetching schedules:", err);
    }
  }, [callApi]);

  // Real-time Firestore listener + fallback (Zero polling)
  useEffect(() => {
    let isMounted = true;
    let unsub = null;

    if (db) {
      try {
        const q = query(
          collection(db, "schedules"),
          where("status", "==", "pending")
        );
        unsub = onSnapshot(
          q,
          (snap) => {
            if (!isMounted) return;
            const items = snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => Number(a.executeAt || 0) - Number(b.executeAt || 0));
            setPendingSchedules(items);
          },
          (err) => {
            console.warn("[Prices] schedules onSnapshot error, falling back to API once:", err);
            if (isMounted) fetchPendingSchedules();
          }
        );
      } catch (err) {
        console.warn("[Prices] setup listener error:", err);
        fetchPendingSchedules();
      }
    } else {
      fetchPendingSchedules();
    }

    return () => {
      isMounted = false;
      if (unsub) unsub();
    };
  }, [db, fetchPendingSchedules]);

  // Derived calculation for standard total
  const standardTotal = Number(basePrice || 0) + Number(serviceFee || 0);

  // Pending Fee Schedules filter
  const pendingFeeSchedules = useMemo(() => {
    return pendingSchedules.filter(
      (s) => s.category === "fee" || s.updates?.fee !== undefined
    );
  }, [pendingSchedules]);

  // Dirty checks for Panel 1
  const isBasePriceDirty =
    configs?.price !== undefined && Number(basePrice) !== Number(configs.price);
  const isServiceFeeDirty =
    configs?.fee !== undefined && Number(serviceFee) !== Number(configs.fee);

  // Dirty checks for Panel 2 (Special Prices)
  const isSpecialPricesDirty = useMemo(() => {
    const origKeys = Object.keys(originalSpecialPrices);
    const currKeys = Object.keys(specialPricesMap);
    if (origKeys.length !== currKeys.length) return true;
    return currKeys.some(
      (k) => Number(specialPricesMap[k]) !== Number(originalSpecialPrices[k])
    );
  }, [specialPricesMap, originalSpecialPrices]);

  // Dirty checks for Bank accounts
  const origThaiBankName = configs?.payment?.thaiBank?.bankName || "ธนาคารกสิกรไทย";
  const origThaiAccName = configs?.payment?.thaiBank?.accName || "บจก. วันดี ออนไลน์";
  const origThaiAccNo = configs?.payment?.thaiBank?.accNo || "210-1-08629-4";
  const isThaiBankChanged =
    thaiBankName !== origThaiBankName ||
    thaiAccName !== origThaiAccName ||
    thaiAccNo !== origThaiAccNo;

  const origLaoLakBankName =
    configs?.payment?.laoBankLak?.bankName ||
    configs?.payment?.laoBank?.name ||
    "Joint Development Bank (JDB)";
  const origLaoLakAccName =
    configs?.payment?.laoBankLak?.accName ||
    configs?.payment?.laoBank?.accName ||
    "MISS THITAPA THANASAPPREECHA";
  const origLaoLakAccNo =
    configs?.payment?.laoBankLak?.accNo ||
    configs?.payment?.laoBank?.accNo ||
    "00120010010080447";
  const isLaoLakBankChanged =
    laoLakBankName !== origLaoLakBankName ||
    laoLakAccName !== origLaoLakAccName ||
    laoLakAccNo !== origLaoLakAccNo;

  const origLaoThbBankName =
    configs?.payment?.laoBankThb?.bankName || "Joint Development Bank (JDB)";
  const origLaoThbAccName =
    configs?.payment?.laoBankThb?.accName || "MISS THITAPA THANASAPPREECHA";
  const origLaoThbAccNo =
    configs?.payment?.laoBankThb?.accNo || "00120010210030720";
  const isLaoThbBankChanged =
    laoThbBankName !== origLaoThbBankName ||
    laoThbAccName !== origLaoThbAccName ||
    laoThbAccNo !== origLaoThbAccNo;

  // Generic config updater
  const updateChange = async (key, value) => {
    setLoading(true);
    try {
      const { status, toastMessage } = await callApi(
        "configs-edit",
        {
          id: "general",
          key: key,
          value: value,
        },
        { notShowLoading: true }
      );

      if (status) {
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: `อัปเดตข้อมูลสำเร็จ`,
        });
        updateConfig();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: toastMessage || `อัปเดตข้อมูลไม่สำเร็จ`,
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: `เกิดข้อผิดพลาดในการบันทึกข้อมูล`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBlurSave = (key, val, currentVal) => {
    if (val !== undefined && val !== null && String(val) !== String(currentVal)) {
      updateChange(key, val);
    }
  };

  // ── Panel 1: Save Base Price & Fee ──
  const handleSaveBasePriceAndFee = async () => {
    setLoading(true);
    try {
      const promises = [];
      if (isBasePriceDirty) {
        promises.push(
          callApi(
            "configs-edit",
            { id: "general", key: "price", value: Number(basePrice) },
            { notShowLoading: true }
          )
        );
      }
      if (isServiceFeeDirty) {
        promises.push(
          callApi(
            "configs-edit",
            { id: "general", key: "fee", value: Number(serviceFee) },
            { notShowLoading: true }
          )
        );
      }
      const results = await Promise.all(promises);
      if (results.every((r) => r?.status)) {
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: "อัปเดตราคาพื้นฐานและค่าบริการสำเร็จ",
        });
        updateConfig();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: "บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการบันทึกราคาพื้นฐาน",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Save Fee Schedule to Universal Queue ──
  const handleSaveFeeSchedule = async (e) => {
    if (e) e.preventDefault();
    const newFeeNum = Number(feeScheduleTarget);
    if (isNaN(newFeeNum) || newFeeNum < 0) {
      alert("กรุณาระบุค่าบริการใหม่ที่ถูกต้อง");
      return;
    }
    if (!feeScheduleStart) {
      alert("กรุณาระบุเวลาที่เริ่มมีผล");
      return;
    }

    const startMs = new Date(feeScheduleStart).getTime();
    if (isNaN(startMs)) {
      alert("รูปแบบเวลาเริ่มไม่ถูกต้อง");
      return;
    }

    const now = Date.now();
    if (startMs <= now) {
      alert("เวลาที่เริ่มมีผลต้องเป็นเวลาในอนาคต");
      return;
    }

    setLoading(true);
    try {
      await callApi("schedules-create", {
        title: `ปรับค่าบริการสลาก (Fee) เป็น ${newFeeNum} ฿`,
        category: "fee",
        action: "update_config",
        executeAt: startMs,
        updates: {
          fee: newFeeNum,
        },
      });

      toast({
        className: "bg-[rgb(59,176,101)] text-white font-semibold",
        title: "ตั้งเวลาเปลี่ยนค่าบริการสำเร็จ",
      });
      fetchPendingSchedules();
      setIsFeeScheduleModalOpen(false);
      setFeeScheduleTarget("");
      setFeeScheduleStart("");
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการตั้งเวลาค่าบริการ",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Panel 2: Live Parsed Preview & Add Special Prices (Real-time or Scheduled) ──
  const previewPatterns = useMemo(() => {
    return parseInputToPatterns(inputNumber);
  }, [inputNumber]);

  const adjNumber = Number(inputAdj) || 0;
  const previewFinalPrice = Math.max(0, standardTotal + adjNumber);

  const handleAddSpecial = async (e) => {
    if (e) e.preventDefault();
    if (previewPatterns.length === 0) {
      alert("กรุณากรอกตัวเลข (เช่น 68 สำหรับท้าย 2 ตัว หรือ 999 สำหรับท้าย 3 ตัว)");
      return;
    }
    if (inputAdj === "" || isNaN(adjNumber)) {
      alert("กรุณากรอกจำนวนเงินปรับราคาที่ถูกต้อง (เช่น +20 หรือ -10)");
      return;
    }

    // กรณีเลือก "ตั้งเวลาเปลี่ยนราคาล่วงหน้า (Universal Queue)"
    if (isScheduleOpen && (scheduleStart || scheduleEnd)) {
      const now = Date.now();
      const startMs = scheduleStart ? new Date(scheduleStart).getTime() : now;
      const endMs = scheduleEnd ? new Date(scheduleEnd).getTime() : null;

      if (endMs && endMs <= startMs) {
        alert("เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น");
        return;
      }

      setLoading(true);
      try {
        const promises = [];
        for (const p of previewPatterns) {
          // 1. ถ้าเวลาเริ่มอยู่ในอนาคต (มากกว่าปัจจุบัน 1 นาที) ให้ส่งคิวเริ่มเปลี่ยนราคา
          if (startMs > now + 60000) {
            promises.push(
              callApi("schedules-create", {
                title: `ปรับราคาเลข ${p} เป็น ${adjNumber > 0 ? `+${adjNumber}` : adjNumber} ฿`,
                category: "price",
                action: "update_config",
                updates: {
                  [`specialPrices.${p}`]: adjNumber,
                },
                executeAt: startMs,
              })
            );
          } else {
            // ถ้าเวลาเริ่มคือทันที ให้ปรับเข้าตารางปัจจุบันทันที
            setSpecialPricesMap((prev) => ({
              ...prev,
              [p]: adjNumber,
            }));
          }

          // 2. ถ้ามีการระบุเวลาสิ้นสุด ให้ส่งคิวนำออกเมื่อหมดเวลา ("ให้หายไปเลย")
          if (endMs) {
            promises.push(
              callApi("schedules-create", {
                title: `นำราคาเลข ${p} ออก (หมดเวลา)`,
                category: "price",
                action: "delete_config_key",
                deleteKeys: [`specialPrices.${p}`],
                executeAt: endMs,
              })
            );
          }
        }

        await Promise.all(promises);
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: `ตั้งคิวงาน Universal Queue (${previewPatterns.length} รายการ) สำเร็จ`,
        });

        fetchPendingSchedules();
        setInputNumber("");
        setIsScheduleOpen(false);
        setScheduleStart("");
        setScheduleEnd("");
      } catch (err) {
        console.error(err);
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: "เกิดข้อผิดพลาดในการตั้งคิวงาน",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    // กรณีปกติ (ไม่มีการตั้งเวลา มีผลทันทีตลอดไป)
    setSpecialPricesMap((prev) => {
      const next = { ...prev };
      previewPatterns.forEach((p) => {
        next[p] = adjNumber;
      });
      return next;
    });

    const actionText =
      adjNumber < 0 ? `ลดราคา ${Math.abs(adjNumber)} ฿` : `บวกเพิ่ม ${adjNumber} ฿`;
    toast({
      className: "bg-[rgb(59,176,101)] text-white font-semibold",
      title: `เพิ่ม ${previewPatterns.length} รายการ (${actionText}) เรียบร้อย`,
    });

    setInputNumber("");
  };

  const handleRemoveSpecial = (pattern) => {
    if (!window.confirm(`ต้องการลบเลข ${pattern} ใช่หรือไม่?`)) return;
    setSpecialPricesMap((prev) => {
      const next = { ...prev };
      delete next[pattern];
      return next;
    });
  };

  const handleSaveInlineSpecial = (pattern) => {
    const num = Number(editingValue);
    if (isNaN(num)) {
      alert("กรุณากรอกตัวเลขจำนวนเงินที่ถูกต้อง");
      return;
    }
    setSpecialPricesMap((prev) => ({
      ...prev,
      [pattern]: num,
    }));
    setEditingKey(null);
  };

  const handleSaveSpecialPrices = async () => {
    setLoading(true);
    try {
      const { status } = await callApi(
        "configs-edit",
        {
          id: "general",
          key: "specialPrices",
          value: specialPricesMap,
        },
        { notShowLoading: true }
      );

      if (status) {
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: "บันทึกการตั้งค่าราคาเฉพาะเลขสำเร็จ",
        });
        setOriginalSpecialPrices({ ...specialPricesMap });
        updateConfig();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: "บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevertSpecialPrices = () => {
    if (!window.confirm("ยกเลิกการแก้ไขและกลับไปใช้ค่าเดิมใช่หรือไม่?")) return;
    setSpecialPricesMap({ ...originalSpecialPrices });
  };

  // Cancel a scheduled job in Universal Queue
  const handleCancelSchedule = async (jobId, title) => {
    if (!window.confirm(`ต้องการยกเลิกคิวงาน "${title || jobId}" ใช่หรือไม่?`)) return;
    setLoading(true);
    try {
      const res = await callApi("schedules-cancel", { id: jobId });
      if (res && res.status) {
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: "ยกเลิกคิวงานสำเร็จ",
        });
        fetchPendingSchedules();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: res?.message || "ยกเลิกไม่สำเร็จ",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Force Run Queue immediately
  const [isRunningQueue, setIsRunningQueue] = useState(false);
  const handleForceRunQueue = async () => {
    try {
      setIsRunningQueue(true);
      const res = await callApi("schedules-run");
      if (res && res.status) {
        toast({
          className: "bg-[rgb(59,176,101)] text-white font-semibold",
          title: `ประมวลผลคิวสำเร็จ (รันไป ${res.executedCount || 0} งาน)`,
        });
        fetchPendingSchedules();
      } else {
        toast({
          className: "bg-[#c15b78] text-white font-semibold",
          title: res?.message || "สั่งรันคิวไม่สำเร็จ",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        className: "bg-[#c15b78] text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการสั่งรันคิว",
      });
    } finally {
      setIsRunningQueue(false);
    }
  };

  // Filter & Display Special Numbers List
  const displaySpecialList = useMemo(() => {
    let list = Object.entries(specialPricesMap).map(([pattern, fee]) => {
      const numFee = Number(fee);
      const info = getPatternInfo(pattern);
      return {
        pattern,
        fee: numFee,
        totalPrice: Math.max(0, standardTotal + numFee),
        isDiscount: numFee < 0,
        isIncrease: numFee > 0,
        ...info,
      };
    });

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => item.pattern.toLowerCase().includes(q));
    }

    if (filterType !== "all") {
      if (filterType === "discount") {
        list = list.filter((i) => i.isDiscount);
      } else if (filterType === "increase") {
        list = list.filter((i) => i.isIncrease);
      } else {
        list = list.filter((i) => i.type === filterType);
      }
    }

    list.sort((a, b) => a.pattern.localeCompare(b.pattern));
    return list;
  }, [specialPricesMap, searchQuery, filterType, standardTotal]);

  const totalSpecialCount = Object.keys(specialPricesMap).length;

  return (
    <div className="h-fit w-full pb-16">
      {/* Page Header */}
      <Header
        header="Settings"
        subHeader="ตั้งค่าราคาลอตเตอรี่ ราคาเฉพาะเลข ค่าเงิน และบัญชีรับเงินโอน"
        icon={<FiSettings className="text-[#002C7B]" />}
      />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center backdrop-blur-[1px]">
          <div className="bg-white p-4 rounded-xl shadow-lg flex items-center gap-3 border border-gray-100">
            <Spinner color="primary" style={{ width: "24px", height: "24px" }} />
            <span className="text-sm font-bold text-gray-700">กำลังประมวลผลข้อมูล...</span>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          PANEL 1: ราคาลอตเตอรี่และค่าบริการ (Base Price & Fee)
      ───────────────────────────────────────────────────────────── */}
      <div className="relative rounded-lg mt-6 overflow-hidden shadow-md bg-white w-full border border-gray-100">
        <div className="p-3.5 bg-slate-50 border-b border-[#E5E7E9] flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded bg-[#002C7B]/10 text-[#002C7B]">
              <FiDollarSign className="text-sm" />
            </span>
            <p className="font-bold text-sm text-[#002C7B]">
              1. ราคาลอตเตอรี่และค่าบริการ (Base Price & Fee)
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsFeeScheduleModalOpen(true)}
              className="flex items-center gap-1.5 py-1 px-3 rounded text-xs font-bold bg-blue-50 hover:bg-blue-100 text-[#002C7B] border border-blue-200 transition-colors cursor-pointer"
            >
              <FiClock className="text-xs" />
              <span>ตั้งเวลาเปลี่ยนค่าบริการ</span>
              {pendingFeeSchedules.length > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-extrabold bg-amber-500 text-white animate-pulse">
                  {pendingFeeSchedules.length}
                </span>
              )}
            </button>

            {(isBasePriceDirty || isServiceFeeDirty) && (
              <>
                <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded">
                  มีการแก้ไขราคาพื้นฐาน
                </span>
                <button
                  type="button"
                  onClick={handleSaveBasePriceAndFee}
                  disabled={loading}
                  className="py-1 px-3 rounded text-xs font-bold bg-[#002C7B] hover:bg-[#2065C4] text-white transition-colors"
                >
                  บันทึกราคานี้
                </button>
              </>
            )}
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
          {/* Base Price Input */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              ราคาลอตเตอรี่ (Price):
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                value={basePrice}
                onChange={(e) =>
                  setBasePrice(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full pl-3.5 pr-12 py-2 rounded-md border border-gray-300 font-bold text-base text-gray-800 focus:outline-none focus:border-[#002C7B] focus:ring-1 focus:ring-[#002C7B]"
              />
              <span className="absolute right-3.5 top-2.5 text-xs font-bold text-gray-400">
                บาท
              </span>
            </div>
          </div>

          {/* Service Fee Input */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              ราคาค่าบริการ (Fee):
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                value={serviceFee}
                onChange={(e) =>
                  setServiceFee(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full pl-3.5 pr-12 py-2 rounded-md border border-gray-300 font-bold text-base text-gray-800 focus:outline-none focus:border-[#002C7B] focus:ring-1 focus:ring-[#002C7B]"
              />
              <span className="absolute right-3.5 top-2.5 text-xs font-bold text-gray-400">
                บาท
              </span>
            </div>
          </div>

          {/* Normal Total Box */}
          <div className="sm:pt-5">
            <div className="bg-slate-50 border border-gray-200 rounded-md p-2.5 flex items-center justify-between">
              <div>
                <span className="text-[11px] text-gray-500 font-medium block">
                  ราคาสลากรวมปกติ:
                </span>
                <span className="text-xs text-gray-400">
                  สลาก {basePrice || 0} ฿ + บริการ {serviceFee || 0} ฿
                </span>
              </div>
              <span className="text-xl font-bold text-[#002C7B]">
                {standardTotal} ฿
              </span>
            </div>
          </div>
        </div>

        {/* Active Fee Schedule Notification in Panel 1 */}
        {pendingFeeSchedules.length > 0 && (
          <div className="mx-5 mb-5 p-3 rounded-md bg-amber-50/80 border border-amber-200 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="p-1 rounded bg-amber-500 text-white">
                <FiClock className="text-xs" />
              </span>
              <div>
                <span className="font-bold text-amber-950">
                  มีคิวตั้งเวลาเปลี่ยนค่าบริการ ({pendingFeeSchedules.length} งาน):
                </span>{" "}
                <span className="text-amber-800 font-semibold">
                  {pendingFeeSchedules
                    .map(
                      (s) =>
                        `${s.title} (${formatScheduleTime(s.executeAt)})`
                    )
                    .join(" • ")}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsFeeScheduleModalOpen(true)}
              className="text-xs font-bold text-amber-900 hover:text-amber-950 underline cursor-pointer"
            >
              จัดการคิวค่าบริการ
            </button>
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          PANEL 2: ปรับราคาเฉพาะเลขและส่วนลด (Special Number Pricing)
      ───────────────────────────────────────────────────────────── */}
      <div className="relative rounded-lg mt-6 overflow-hidden shadow-md bg-white w-full border border-gray-100">
        <div className="p-3.5 bg-slate-50 border-b border-[#E5E7E9] flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded bg-[#002C7B]/10 text-[#002C7B]">
              <FiTag className="text-sm" />
            </span>
            <p className="font-bold text-sm text-[#002C7B]">
              2. ปรับราคาเฉพาะเลขและส่วนลด (Special Number Pricing & Discounts)
            </p>
          </div>

          <div className="text-[11px] text-gray-500 flex items-center gap-1">
            <FiHelpCircle className="text-gray-400" />
            <span>พิมพ์ 2 หลัก = ท้าย 2 ตัว, 3 หลัก = ท้าย 3 ตัว</span>
          </div>
        </div>

        {/* 2-Field Add Form + Schedule Drawer */}
        <form onSubmit={handleAddSpecial} className="p-5 border-b border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
            {/* Field 1: ตัวเลข (sm: 6 cols) */}
            <div className="sm:col-span-6">
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                เลขที่ต้องการตั้งราคา:
              </label>
              <input
                type="text"
                placeholder="เช่น 68 หรือ 999 หรือ 15, 68, 95"
                value={inputNumber}
                onChange={(e) => setInputNumber(e.target.value)}
                className="w-full px-3.5 py-2 rounded-md border border-gray-300 font-mono text-base font-bold text-gray-800 focus:outline-none focus:border-[#002C7B] focus:ring-1 focus:ring-[#002C7B]"
              />
            </div>

            {/* Field 2: ปรับราคา (sm: 3 cols) */}
            <div className="sm:col-span-3">
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                ปรับราคา (บาท):
              </label>
              <div className="relative">
                <input
                  type="number"
                  placeholder="+20 หรือ -10"
                  value={inputAdj}
                  onChange={(e) => setInputAdj(e.target.value)}
                  className={`w-full pl-3 pr-10 py-2 rounded-md border font-bold text-base focus:outline-none ${
                    adjNumber < 0
                      ? "border-emerald-500 text-emerald-600 bg-emerald-50/20"
                      : adjNumber > 0
                      ? "border-blue-400 text-[#002C7B]"
                      : "border-gray-300 text-gray-800"
                  }`}
                />
                <span className="absolute right-3 top-2.5 text-xs font-bold text-gray-400">
                  ฿
                </span>
              </div>
            </div>

            {/* Submit Button (sm: 3 cols) */}
            <div className="sm:col-span-3">
              <button
                type="submit"
                className="w-full bg-[#002C7B] hover:bg-[#2065C4] text-white font-bold text-sm py-2 px-4 rounded-md shadow-sm transition-colors flex items-center justify-center gap-1.5 h-[42px]"
              >
                {isScheduleOpen && (scheduleStart || scheduleEnd) ? (
                  <>
                    <FiClock /> ตั้งคิวงานล่วงหน้า
                  </>
                ) : (
                  <>
                    <FiPlus /> เพิ่มรายการ
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quick Helper Bar */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-gray-500">ผลลัพธ์:</span>
              {previewPatterns.length > 0 ? (
                previewPatterns.map((p) => (
                  <span
                    key={p}
                    className="font-mono font-bold px-2 py-0.5 rounded bg-blue-50 text-[#002C7B] border border-blue-200"
                  >
                    {p}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 italic">(ยังไม่ได้กรอกเลข)</span>
              )}

              {previewPatterns.length > 0 && (
                <span className="text-gray-600 ml-1">
                  → ราคาสุทธิ:{" "}
                  <b className={adjNumber < 0 ? "text-emerald-600" : "text-gray-900"}>
                    {previewFinalPrice} ฿
                  </b>{" "}
                  ({adjNumber < 0 ? `ลด ${Math.abs(adjNumber)} ฿` : adjNumber > 0 ? `+${adjNumber} ฿` : "ปกติ"})
                </span>
              )}
            </div>

            {/* Quick Price Buttons & Schedule Toggle */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-400 mr-1">ปุ่มด่วน:</span>
                {[-10, -5, 10, 20, 50].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setInputAdj(String(val))}
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded border transition-colors ${
                      Number(inputAdj) === val
                        ? val < 0
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-[#002C7B] text-white border-[#002C7B]"
                        : val < 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {val > 0 ? `+${val}` : val} ฿
                  </button>
                ))}
              </div>

              {/* Toggle Schedule Option */}
              <button
                type="button"
                onClick={() => setIsScheduleOpen(!isScheduleOpen)}
                className={`py-1 px-2.5 rounded text-xs font-semibold border flex items-center gap-1.5 transition-colors ${
                  isScheduleOpen
                    ? "bg-[#002C7B] text-white border-[#002C7B]"
                    : "bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100"
                }`}
              >
                <FiClock className="text-xs" />
                <span>{isScheduleOpen ? "ปิดตั้งเวลา" : "ตั้งเวลาล่วงหน้า"}</span>
              </button>
            </div>
          </div>

          {/* Collapsible Schedule Drawer (Universal Queue Integration) */}
          {isScheduleOpen && (
            <div className="mt-4 p-4 rounded-lg bg-blue-50/40 border border-blue-200 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="p-1 rounded bg-[#002C7B] text-white">
                    <FiCalendar className="text-xs" />
                  </span>
                  <span className="font-bold text-xs text-[#002C7B]">
                    ตั้งเวลาเปลี่ยนราคาล่วงหน้า (Universal Queue)
                  </span>
                </div>
                <span className="text-[11px] text-gray-500">
                  ระบบจะปรับราคาและลบออกให้อัตโนมัติเมื่อถึงเวลา
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Start Date-Time */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-700 mb-1">
                    เริ่มมีผลเวลา (Start Time):
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleStart}
                    onChange={(e) => setScheduleStart(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-md border border-gray-300 text-xs font-semibold focus:outline-none focus:border-[#002C7B] bg-white"
                  />
                  <span className="text-[10px] text-gray-400 mt-0.5 block">
                    (เว้นว่างไว้หากต้องการให้เริ่มมีผลทันที)
                  </span>
                </div>

                {/* End Date-Time */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-700 mb-1">
                    สิ้นสุด / นำออก (End Time):
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleEnd}
                    onChange={(e) => setScheduleEnd(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-md border border-gray-300 text-xs font-semibold focus:outline-none focus:border-[#002C7B] bg-white"
                  />
                  <span className="text-[10px] text-gray-400 mt-0.5 block">
                    (เมื่อถึงเวลาสิ้นสุด ราคาจะถูกนำออกและหายไปจากระบบอัตโนมัติ)
                  </span>
                </div>
              </div>
            </div>
          )}
        </form>
        {/* ── Scheduled Tasks Section (Universal Queue - แสดงตลอดเวลา ไม่ซ่อน) ── */}
        <div className="p-4 bg-amber-50/40 border-b border-amber-200">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-md bg-amber-500 text-white shadow-2xs">
                <FiClock className="text-sm" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-xs sm:text-sm text-slate-900">
                    รายการตั้งเวลาล่วงหน้า (Scheduled Tasks)
                  </h4>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                      pendingSchedules.length > 0
                        ? "bg-amber-500 text-white animate-pulse"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {pendingSchedules.length} งานรอทำงาน
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  ระบบ Universal Queue จะทำการปรับใช้ราคาหรือนำออกให้อัตโนมัติเมื่อถึงเวลาที่กำหนด
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleForceRunQueue}
                disabled={loading || isRunningQueue || pendingSchedules.length === 0}
                className="flex items-center gap-1 text-xs font-bold text-amber-900 bg-amber-200 hover:bg-amber-300 active:scale-95 px-2.5 py-1 rounded border border-amber-300 transition shadow-2xs cursor-pointer disabled:opacity-50"
                title="ประมวลผลงานที่ถึงเวลาแล้วทันทีโดยไม่ต้องรอรอบอัตโนมัติ"
              >
                <FiZap className={`text-xs ${isRunningQueue ? "animate-spin" : ""}`} />
                <span>{isRunningQueue ? "กำลังรัน..." : "รันคิวทันที"}</span>
              </button>

              <button
                type="button"
                onClick={() => setIsScheduleOpen(!isScheduleOpen)}
                className="text-xs font-bold text-blue-700 hover:text-blue-900 bg-white hover:bg-blue-50 px-2.5 py-1 rounded border border-blue-200 transition shadow-2xs cursor-pointer"
              >
                {isScheduleOpen ? "ปิดฟอร์มตั้งเวลา" : "+ ตั้งเวลาใหม่"}
              </button>
            </div>
          </div>

          {pendingSchedules.length === 0 ? (
            <div className="p-4 bg-white/80 rounded-md border border-dashed border-amber-200 text-center flex flex-col items-center justify-center gap-1.5 text-xs text-slate-500">
              <FiClock className="text-amber-400 text-xl" />
              <span className="font-semibold text-slate-700">ขณะนี้ไม่มีรายการตั้งเวลาล่วงหน้า</span>
              <span className="text-[11px] text-slate-400">
                คุณสามารถตั้งเวลาปรับราคาหรือกำหนดเวลาหมดอายุล่วงหน้าได้ โดยกดที่ปุ่ม "ตั้งเวลาล่วงหน้า" ในฟอร์มด้านบน
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-amber-200 bg-white shadow-2xs">
              <table className="w-full text-left text-xs">
                <thead className="bg-amber-100/60 text-amber-950 font-bold border-b border-amber-200 text-[11px]">
                  <tr>
                    <th className="py-2 px-3">#</th>
                    <th className="py-2 px-3">ชื่องาน / รายละเอียด</th>
                    <th className="py-2 px-3">การกระทำ</th>
                    <th className="py-2 px-3">กำหนดทำงาน (กี่โมง)</th>
                    <th className="py-2 px-3 text-center">สถานะ</th>
                    <th className="py-2 px-3 text-right">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100 font-semibold text-slate-700">
                  {pendingSchedules.map((job, idx) => (
                    <tr key={job.id} className="hover:bg-amber-50/40 transition">
                      <td className="py-2 px-3 text-slate-400 font-mono text-[11px]">
                        {idx + 1}
                      </td>
                      <td className="py-2 px-3 font-bold text-slate-900">
                        {job.title}
                      </td>
                      <td className="py-2 px-3">
                        {job.action === "update_config" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                            <FiTrendingUp className="text-[10px]" /> ปรับราคาพิเศษ
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                            <FiTrash2 className="text-[10px]" /> นำราคาพิเศษออก
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 font-bold text-amber-800">
                        {formatScheduleTime(job.executeAt)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                          รอทำงาน
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {Number(job.executeAt || 0) <= Date.now() && (
                            <button
                              type="button"
                              onClick={handleForceRunQueue}
                              disabled={loading || isRunningQueue}
                              className="px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold transition shadow-2xs hover:scale-95 cursor-pointer disabled:opacity-50 flex items-center gap-1"
                              title="งานนี้ถึงเวลาแล้ว กดเพื่อสั่งรันทันที"
                            >
                              <FiZap className="text-[10px]" />
                              <span>รันตอนนี้</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCancelSchedule(job.id, job.title)}
                            disabled={loading}
                            className="px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-[11px] font-bold transition shadow-2xs hover:scale-95 cursor-pointer disabled:opacity-50"
                          >
                            ยกเลิกคิว
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Table Toolbar */}
        <div className="p-4 bg-slate-50/70 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="font-bold text-sm text-[#002C7B]">
              รายการที่ตั้งค่า ({displaySpecialList.length}/{totalSpecialCount})
            </span>
            {isSpecialPricesDirty && (
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 animate-pulse">
                มีรายการที่ยังไม่ได้บันทึก
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
              <input
                type="text"
                placeholder="ค้นหาเลข..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs rounded-md border border-gray-300 w-36 sm:w-44 focus:outline-none focus:border-[#002C7B]"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="text-xs py-1.5 px-2.5 rounded-md border border-gray-300 font-semibold focus:outline-none focus:border-[#002C7B]"
            >
              <option value="all">ทั้งหมด</option>
              <option value="end2">ท้าย 2 ตัว</option>
              <option value="end3">ท้าย 3 ตัว</option>
              <option value="exact6">6 หลักตรง</option>
              <option value="discount">เฉพาะลดราคา (-)</option>
              <option value="increase">เฉพาะบวกเพิ่ม (+)</option>
            </select>

            {isSpecialPricesDirty && (
              <button
                type="button"
                onClick={handleRevertSpecialPrices}
                disabled={loading}
                title="ยกเลิกการเปลี่ยนแปลง"
                className="py-1.5 px-2.5 rounded-md text-xs font-semibold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <FiRotateCcw /> คืนค่า
              </button>
            )}

            <button
              type="button"
              onClick={handleSaveSpecialPrices}
              disabled={loading || !isSpecialPricesDirty}
              className={`py-1.5 px-3.5 rounded-md text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                isSpecialPricesDirty
                  ? "bg-[#002C7B] hover:bg-[#2065C4] text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              <FiSave /> บันทึกราคาเฉพาะเลข {isSpecialPricesDirty && `(${Object.keys(specialPricesMap).length})`}
            </button>
          </div>
        </div>

        {/* Minimal Clean Special Prices Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-200 text-gray-500 text-xs uppercase font-bold tracking-wider">
                <th className="py-2.5 px-4 text-center w-12">#</th>
                <th className="py-2.5 px-4">เลข / รูปแบบ</th>
                <th className="py-2.5 px-4">ประเภท</th>
                <th className="py-2.5 px-4 text-right">ปรับราคา</th>
                <th className="py-2.5 px-4 text-right">ราคาสุทธิที่ขาย</th>
                <th className="py-2.5 px-4 text-center w-20">จัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm font-semibold">
              {displaySpecialList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400 text-xs">
                    {searchQuery || filterType !== "all"
                      ? "ไม่พบเลขที่ค้นหา"
                      : "ยังไม่มีการตั้งค่าราคาเฉพาะเลข (กรอกเลขด้านบนแล้วกดเพิ่มได้เลย)"}
                  </td>
                </tr>
              ) : (
                displaySpecialList.map((item, index) => {
                  const isEditing = editingKey === item.pattern;
                  const isChanged =
                    originalSpecialPrices[item.pattern] !== undefined &&
                    Number(originalSpecialPrices[item.pattern]) !== Number(item.fee);
                  const isNew = originalSpecialPrices[item.pattern] === undefined;

                  return (
                    <tr
                      key={item.pattern}
                      className={`hover:bg-slate-50/80 transition-colors ${
                        isNew ? "bg-emerald-50/30" : isChanged ? "bg-amber-50/30" : ""
                      }`}
                    >
                      <td className="py-2.5 px-4 text-center text-xs text-gray-400">
                        {index + 1}
                      </td>

                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-base font-bold text-gray-800 tracking-wider">
                            {item.pattern}
                          </span>
                          {pendingSchedules.some(
                            (s) =>
                              s.updates?.[`specialPrices.${item.pattern}`] !== undefined ||
                              s.deleteKeys?.includes(`specialPrices.${item.pattern}`)
                          ) && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.2 rounded"
                              title="มีคิวงานตั้งเวลาล่วงหน้าที่รอทำงานสำหรับเลขนี้"
                            >
                              <FiClock className="text-[10px]" />
                              ตั้งเวลาไว้
                            </span>
                          )}
                          {isNew && (
                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100/70 px-1.5 py-0.2 rounded">
                              ใหม่
                            </span>
                          )}
                          {isChanged && (
                            <span className="text-[10px] font-bold text-amber-600 bg-amber-100/70 px-1.5 py-0.2 rounded">
                              แก้ไข
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-2.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border ${item.badgeClass}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${item.dotClass}`} />
                          {item.label}
                        </span>
                      </td>

                      <td className="py-2.5 px-4 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              autoFocus
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveInlineSpecial(item.pattern);
                                if (e.key === "Escape") setEditingKey(null);
                              }}
                              className="w-20 px-2 py-0.5 text-right text-sm font-bold border border-[#002C7B] rounded focus:outline-none"
                            />
                            <button
                              onClick={() => handleSaveInlineSpecial(item.pattern)}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            >
                              <FiCheck className="text-base" />
                            </button>
                            <button
                              onClick={() => setEditingKey(null)}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                            >
                              <FiX className="text-base" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1.5 group">
                            {item.isDiscount ? (
                              <span className="inline-flex items-center gap-1 font-bold text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                <FiTrendingDown /> {item.fee} ฿
                              </span>
                            ) : item.isIncrease ? (
                              <span className="inline-flex items-center gap-1 font-bold text-xs text-[#002C7B] bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                                <FiTrendingUp /> +{item.fee} ฿
                              </span>
                            ) : (
                              <span className="text-xs text-gray-500 font-semibold">0 ฿</span>
                            )}
                            <button
                              onClick={() => {
                                setEditingKey(item.pattern);
                                setEditingValue(String(item.fee));
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-[#002C7B] hover:bg-gray-100 rounded transition-opacity"
                              title="คลิกเพื่อแก้ไขราคา"
                            >
                              <FiEdit2 className="text-xs" />
                            </button>
                          </div>
                        )}
                      </td>

                      <td className="py-2.5 px-4 text-right">
                        <span
                          className={`font-bold text-sm ${
                            item.isDiscount ? "text-emerald-700" : "text-gray-900"
                          }`}
                        >
                          {item.totalPrice} ฿
                        </span>
                      </td>

                      <td className="py-2.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecial(item.pattern)}
                          className="p-1 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                          title="ลบ"
                        >
                          <FiTrash2 className="text-sm" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          PANEL 3: ช่องทางการชำระเงินและอัตราแลกเปลี่ยน
      ───────────────────────────────────────────────────────────── */}
      <div className="relative rounded-lg mt-6 shadow-md bg-white w-full border border-gray-100 overflow-hidden">
        <div className="p-3.5 bg-slate-50 border-b border-[#E5E7E9] flex items-center gap-2">
          <span className="p-1 rounded bg-[#002C7B]/10 text-[#002C7B]">
            <FiGlobe className="text-sm" />
          </span>
          <p className="font-bold text-sm text-[#002C7B]">
            3. ช่องทางการชำระเงินและอัตราแลกเปลี่ยน
          </p>
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ช่องทางการจ่ายเงินเริ่มต้น</p>
          <select
            className="border border-gray-300 py-1.5 px-3 font-semibold text-sm rounded-lg focus:outline-none focus:border-blue-500 disabled:opacity-65"
            disabled={loading}
            value={configs?.payment?.defaultPayment || "bank"}
            onChange={(e) => updateChange("payment.defaultPayment", e.target.value)}
          >
            <option value={"bank"}>โอนเงิน-แนบสลิป</option>
            <option value={"qr"} disabled={!configs?.isOpen?.qrPayment}>สแกน QR</option>
          </select>
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">เปิดชำระด้วย QR</p>
          <Switch
            type="switch"
            disabled={!configs || loading}
            checked={Boolean(configs?.isOpen?.qrPayment)}
            onCheckedChange={(e) => updateChange("isOpen.qrPayment", e)}
          />
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">บัญชีธนาคารสำหรับ QR code</p>
          <select
            className="border border-gray-300 py-1.5 px-3 font-semibold text-sm rounded-lg focus:outline-none focus:border-blue-500 disabled:opacity-65"
            disabled={loading}
            value={configs?.payment?.qrChannel || "cimb"}
            onChange={(e) => updateChange("payment.qrChannel", e.target.value)}
          >
            <option value={"bbl"}>BBL</option>
            <option value={"cimb"}>CIMB</option>
            <option value={"cimb2"}>CIMB2</option>
          </select>
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ค่าเงินกีบ (ลาว - ซื้อ)</p>
          <div className="flex justify-center items-center gap-2">
            <input
              type="number"
              className="w-24 px-2 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500"
              disabled={!configs || loading}
              defaultValue={configs?.currency?.lak}
              onBlur={(e) =>
                handleBlurSave("currency.lak", Number(e.target.value), configs?.currency?.lak)
              }
            />
            <p className="font-semibold text-sm text-gray-600 w-20">กีบ/บาท</p>
          </div>
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ค่าเงินกีบ (ลาว - จ่ายรางวัล)</p>
          <div className="flex justify-center items-center gap-2">
            <input
              type="number"
              className="w-24 px-2 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500"
              disabled={!configs || loading}
              defaultValue={configs?.currency?.lak_reward}
              onBlur={(e) =>
                handleBlurSave(
                  "currency.lak_reward",
                  Number(e.target.value),
                  configs?.currency?.lak_reward
                )
              }
            />
            <p className="font-semibold text-sm text-gray-600 w-20">กีบ/บาท</p>
          </div>
        </div>

        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ค่าเงินวอน (เกาหลี)</p>
          <div className="flex justify-center items-center gap-2">
            <input
              type="number"
              className="w-24 px-2 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500"
              disabled={!configs || loading}
              defaultValue={configs?.currency?.krw}
              onBlur={(e) =>
                handleBlurSave("currency.krw", Number(e.target.value), configs?.currency?.krw)
              }
            />
            <p className="font-semibold text-sm text-gray-600 w-20">วอน/บาท</p>
          </div>
        </div>

        <div className="p-3 flex justify-between items-center">
          <p className="font-semibold text-sm text-gray-700">ค่าเงินหยวน (จีน)</p>
          <div className="flex justify-center items-center gap-2">
            <input
              type="number"
              className="w-24 px-2 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500"
              disabled={!configs || loading}
              defaultValue={configs?.currency?.cny}
              onBlur={(e) =>
                handleBlurSave("currency.cny", Number(e.target.value), configs?.currency?.cny)
              }
            />
            <p className="font-semibold text-sm text-gray-600 w-20">บาท/¥ Yuan</p>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          PANEL 4: บัญชีรับเงินโอนของร้านค้า (Receiving Bank Accounts)
      ───────────────────────────────────────────────────────────── */}
      <div className="relative rounded-lg mt-6 shadow-md bg-white w-full border border-gray-100 overflow-hidden">
        <div className="p-3.5 bg-slate-50 border-b border-[#E5E7E9] flex items-center gap-2">
          <span className="p-1 rounded bg-[#002C7B]/10 text-[#002C7B]">
            <FiCreditCard className="text-sm" />
          </span>
          <div>
            <p className="font-bold text-sm text-[#002C7B]">
              4. ตั้งค่าบัญชีรับเงินโอนของร้านค้า (Receiving Bank Accounts)
            </p>
            <p className="text-xs text-gray-500 font-medium">
              จัดการเลขที่บัญชีและชื่อบัญชีที่จะแสดงให้ลูกค้าโอนเงินในหน้าชำระเงิน
            </p>
          </div>
        </div>

        {/* 1. บัญชีธนาคารไทย */}
        <div className="p-3 bg-[#002C7B]/5 border-b border-[#E5E7E9] flex justify-between items-center h-12">
          <p className="font-bold text-xs text-[#002C7B]">1. บัญชีธนาคารไทย (Kasikorn / Thai Bank)</p>
          {isThaiBankChanged && (
            <button
              onClick={() => {
                updateChange("payment.thaiBank", {
                  bankName: thaiBankName,
                  accName: thaiAccName,
                  accNo: thaiAccNo,
                });
              }}
              className="py-1 px-3 text-xs font-bold rounded bg-[#002C7B] text-white hover:bg-[#2065C4] shadow-sm transition-colors"
            >
              บันทึกบัญชีไทย
            </button>
          )}
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อธนาคาร</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={thaiBankName}
            onChange={(e) => setThaiBankName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อบัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={thaiAccName}
            onChange={(e) => setThaiAccName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">เลขที่บัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm font-mono focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={thaiAccNo}
            onChange={(e) => setThaiAccNo(e.target.value)}
          />
        </div>

        {/* 2. บัญชีธนาคารลาว - สกุลเงินกีบ */}
        <div className="p-3 bg-[#002C7B]/5 border-b border-[#E5E7E9] flex justify-between items-center h-12">
          <p className="font-bold text-xs text-[#002C7B]">
            2. บัญชีธนาคารลาว - สกุลเงินกีบ (JDB LAK)
          </p>
          {isLaoLakBankChanged && (
            <button
              onClick={() => {
                updateChange("payment.laoBankLak", {
                  bankName: laoLakBankName,
                  accName: laoLakAccName,
                  accNo: laoLakAccNo,
                });
              }}
              className="py-1 px-3 text-xs font-bold rounded bg-[#002C7B] text-white hover:bg-[#2065C4] shadow-sm transition-colors"
            >
              บันทึกบัญชีลาว (LAK)
            </button>
          )}
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อธนาคาร</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoLakBankName}
            onChange={(e) => setLaoLakBankName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อบัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoLakAccName}
            onChange={(e) => setLaoLakAccName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">เลขที่บัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm font-mono focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoLakAccNo}
            onChange={(e) => setLaoLakAccNo(e.target.value)}
          />
        </div>

        {/* 3. บัญชีธนาคารลาว - สกุลเงินบาท */}
        <div className="p-3 bg-[#002C7B]/5 border-b border-[#E5E7E9] flex justify-between items-center h-12">
          <p className="font-bold text-xs text-[#002C7B]">
            3. บัญชีธนาคารลาว - สกุลเงินบาท (JDB THB)
          </p>
          {isLaoThbBankChanged && (
            <button
              onClick={() => {
                updateChange("payment.laoBankThb", {
                  bankName: laoThbBankName,
                  accName: laoThbAccName,
                  accNo: laoThbAccNo,
                });
              }}
              className="py-1 px-3 text-xs font-bold rounded bg-[#002C7B] text-white hover:bg-[#2065C4] shadow-sm transition-colors"
            >
              บันทึกบัญชีลาว (THB)
            </button>
          )}
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อธนาคาร</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoThbBankName}
            onChange={(e) => setLaoThbBankName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">ชื่อบัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoThbAccName}
            onChange={(e) => setLaoThbAccName(e.target.value)}
          />
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">เลขที่บัญชี</p>
          <input
            type="text"
            className="w-64 sm:w-80 px-3 py-1 rounded-md border border-gray-300 font-semibold text-sm font-mono focus:outline-none focus:border-blue-500 bg-white text-gray-800"
            value={laoThbAccNo}
            onChange={(e) => setLaoThbAccNo(e.target.value)}
          />
        </div>

        {/* 4. บัญชี Alipay (¥ Yuan) */}
        <div className="p-3 bg-[#002C7B]/5 border-b border-[#E5E7E9] flex justify-between items-center h-12">
          <p className="font-bold text-xs text-[#002C7B]">4. บัญชี Alipay (¥ Yuan)</p>
        </div>
        <div className="p-3 flex justify-between items-center border-b border-[#E5E7E9]">
          <p className="font-semibold text-sm text-gray-700">เปิดชำระด้วย Alipay</p>
          <Switch
            type="switch"
            disabled={!configs || loading}
            checked={Boolean(configs?.isOpen?.alipay)}
            onCheckedChange={(e) => updateChange("isOpen.alipay", e)}
          />
        </div>
        <div className="p-3 flex justify-between items-center">
          <div>
            <p className="font-semibold text-sm text-gray-700">QR Code Alipay (¥ Yuan)</p>
            {configs?.payment?.alipayQrUrl && (
              <img
                src={configs.payment.alipayQrUrl}
                alt="alipay-qr"
                className="w-20 h-20 mt-2 object-contain border rounded-md"
              />
            )}
          </div>
          <label
            htmlFor="alipayQrUpload"
            className="bg-[#002C7B] hover:bg-[#2065C4] flex gap-2 items-center rounded-md py-2 px-3 text-white font-bold text-xs cursor-pointer shadow-sm transition-colors"
          >
            <FaPlus /> อัปโหลด QR
          </label>
          <input
            id="alipayQrUpload"
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              const base64 = await resizeImage(file).catch(() => null);
              if (!base64) {
                return alert("อัปโหลดได้เฉพาะไฟล์รูปภาพ นามสกุล jpg หรือ png");
              }
              setLoading(true);
              const { status } = await callApi(
                "configs-editAlipayQr",
                { base64 },
                { notShowLoading: true }
              );
              if (status) {
                toast({
                  className: "bg-[rgb(59,176,101)] text-white font-semibold",
                  title: "อัปเดต QR Alipay สำเร็จ",
                });
                updateConfig();
              } else {
                toast({
                  className: "bg-[#c15b78] text-white font-semibold",
                  title: "อัปเดต QR Alipay ไม่สำเร็จ",
                });
              }
              setLoading(false);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* ── Modal: ตั้งเวลาเปลี่ยนค่าบริการ (Fee Schedule Modal) ── */}
      {isFeeScheduleModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200 overflow-hidden animate-in fade-in-0 zoom-in-95">
            {/* Modal Header */}
            <div className="p-4 bg-slate-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-md bg-[#002C7B] text-white">
                  <FiClock className="text-sm" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    ตั้งเวลาเปลี่ยนค่าบริการ (Schedule Fee)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    กำหนดเวลาปรับเปลี่ยนค่าบริการล่วงหน้าอัตโนมัติ
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsFeeScheduleModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-md transition"
              >
                <FiX className="text-base" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 space-y-4">
              {/* Snapshot of Current Fee */}
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between">
                <div>
                  <span className="text-[11px] text-slate-500 font-semibold block">
                    ค่าบริการปัจจุบัน (Current Fee):
                  </span>
                  <span className="text-xs text-slate-700 font-bold">
                    สลาก {basePrice} ฿ + บริการ {configs?.fee ?? 20} ฿
                  </span>
                </div>
                <span className="text-base font-extrabold text-[#002C7B]">
                  {configs?.fee ?? 20} ฿
                </span>
              </div>

              {/* Form Inputs */}
              <form onSubmit={handleSaveFeeSchedule} className="space-y-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    ค่าบริการใหม่ที่ต้องการเปลี่ยน (บาท):
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      required
                      placeholder="เช่น 15 หรือ 10"
                      value={feeScheduleTarget}
                      onChange={(e) => setFeeScheduleTarget(e.target.value)}
                      className="w-full pl-3.5 pr-12 py-2 rounded-md border border-gray-300 font-bold text-sm text-slate-800 focus:outline-none focus:border-[#002C7B] focus:ring-1 focus:ring-[#002C7B]"
                    />
                    <span className="absolute right-3.5 top-2.5 text-xs font-bold text-gray-400">
                      บาท
                    </span>
                  </div>
                  {feeScheduleTarget !== "" && !isNaN(Number(feeScheduleTarget)) && (
                    <p className="text-[11px] text-emerald-700 font-bold mt-1">
                      → ราคาสลากรวมช่วงนั้นจะเป็น {Number(basePrice) + Number(feeScheduleTarget)} ฿ (สลาก {basePrice} ฿ + ค่าบริการ {feeScheduleTarget} ฿)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    เริ่มมีผลเวลา:
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={feeScheduleStart}
                    onChange={(e) => setFeeScheduleStart(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 text-xs font-semibold focus:outline-none focus:border-[#002C7B] focus:ring-1 focus:ring-[#002C7B]"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    เมื่อถึงเวลาที่กำหนด ระบบจะปรับค่าบริการเป็นราคานี้ทันที (หากต้องการเปลี่ยนอีกครั้ง สามารถมาตั้งคิวใหม่ได้ตลอดเวลา)
                  </p>
                </div>

                {/* Existing pending fee schedules in modal */}
                {pendingFeeSchedules.length > 0 && (
                  <div className="pt-2 border-t border-slate-100">
                    <span className="text-[11px] font-bold text-slate-600 block mb-1.5">
                      คิวงานเปลี่ยนค่าบริการที่รออยู่ ({pendingFeeSchedules.length}):
                    </span>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {pendingFeeSchedules.map((job) => (
                        <div
                          key={job.id}
                          className="flex items-center justify-between p-2 rounded bg-amber-50/80 border border-amber-200 text-[11px]"
                        >
                          <div className="truncate">
                            <span className="font-bold text-slate-800 block truncate">
                              {job.title}
                            </span>
                            <span className="text-amber-800 font-semibold text-[10px]">
                              {formatScheduleTime(job.executeAt)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCancelSchedule(job.id, job.title)}
                            disabled={loading}
                            className="text-rose-600 hover:text-rose-800 font-bold text-[10px] ml-2 shrink-0 cursor-pointer"
                          >
                            ยกเลิก
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Modal Footer Buttons */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFeeScheduleModalOpen(false)}
                    className="px-3 py-1.5 rounded-md text-xs font-bold text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-1.5 rounded-md text-xs font-bold bg-[#002C7B] hover:bg-[#2065C4] text-white transition shadow-xs cursor-pointer"
                  >
                    บันทึกตั้งเวลา
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prices;
